import React, { useState, useEffect } from 'react';
import './ThemeManagement.css';
import '../ui-master-styles.css';

interface ColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  surface: string;
  border: string;
}

interface ThemePreset {
  id: string;
  name: string;
  description: string;
  palette: ColorPalette;
  colors: string[];
}

const ThemeManagement: React.FC = () => {
  const [colorPalette, setColorPalette] = useState<ColorPalette>({
    primary: '#000000',
    secondary: '#1A1A1A',
    accent: '#333333',
    background: '#FFFFFF',
    text: '#000000',
    surface: '#F8F9FA',
    border: '#E5E5E5'
  });

  const [selectedSwatch, setSelectedSwatch] = useState<string>('primary');

  // Default theme presets
  const themePresets: ThemePreset[] = [
    {
      id: 'minimal-black',
      name: 'Minimal Black ⚫',
      description: 'Clean, minimal black and white design',
      palette: {
        primary: '#000000',
        secondary: '#1A1A1A',
        accent: '#333333',
        background: '#FFFFFF',
        text: '#000000',
        surface: '#F8F9FA',
        border: '#E5E5E5'
      },
      colors: ['#000000', '#1A1A1A', '#333333', '#666666', '#999999']
    },
    {
      id: 'classic-warm',
      name: 'Classic Warm',
      description: 'Traditional warm browns and golds',
      palette: {
        primary: '#8B4513',
        secondary: '#A0522D',
        accent: '#D4AF37',
        background: '#FFFFFF',
        text: '#8B4513',
        surface: '#F8F9FA',
        border: '#E1E1E1'
      },
      colors: ['#8B4513', '#A0522D', '#D4AF37']
    },
    {
      id: 'spring-bloom',
      name: 'Spring Bloom 🌸',
      description: 'Fresh spring greens and delicate pinks',
      palette: {
        primary: '#4CAF50',
        secondary: '#81C784',
        accent: '#FFAB91',
        background: '#FFFFFF',
        text: '#2E7D32',
        surface: '#E8F5E8',
        border: '#C8E6C9'
      },
      colors: ['#4CAF50', '#81C784', '#FFAB91', '#E1BEE7', '#F3E5F5']
    },
    {
      id: 'summer-sunshine',
      name: 'Summer Sunshine 🌻',
      description: 'Bright yellows and warm blues like summer days',
      palette: {
        primary: '#FF9800',
        secondary: '#FFB74D',
        accent: '#87CEEB',
        background: '#FFFFFF',
        text: '#FF5722',
        surface: '#FFFBF0',
        border: '#FFE0B2'
      },
      colors: ['#FF9800', '#FFB74D', '#87CEEB', '#FFEB3B', '#E0F2F1']
    },
    {
      id: 'autumn-warmth',
      name: 'Autumn Warmth 🍂',
      description: 'Warm oranges and deep reds like autumn leaves',
      palette: {
        primary: '#FF5722',
        secondary: '#FF7043',
        accent: '#8D6E63',
        background: '#FFFFFF',
        text: '#D84315',
        surface: '#FBE9E7',
        border: '#FFCCBC'
      },
      colors: ['#FF5722', '#FF7043', '#8D6E63', '#A1887F', '#F5F5DC']
    },
    {
      id: 'winter-magic',
      name: 'Winter Magic ❄️',
      description: 'Cool blues and silvers like winter frost',
      palette: {
        primary: '#0277BD',
        secondary: '#039BE5',
        accent: '#90A4AE',
        background: '#FFFFFF',
        text: '#01579B',
        surface: '#F1F8FF',
        border: '#B3E5FC'
      },
      colors: ['#0277BD', '#039BE5', '#90A4AE', '#E1F5FE', '#F5F5F5']
    },
    {
      id: 'christmas-festive',
      name: 'Christmas Magic 🎄',
      description: 'Festive Christmas reds, greens, and golds with holiday sparkle',
      palette: {
        primary: '#C5282A',
        secondary: '#228B22',
        accent: '#FFD700',
        background: '#FFFFFF',
        text: '#2C4505',
        surface: '#F0FFF0',
        border: '#DDA0DD'
      },
      colors: ['#C5282A', '#228B22', '#FFD700', '#DDA0DD', '#F0FFF0']
    },
    {
      id: 'elegant-purple',
      name: 'Elegant Purple',
      description: 'Soft purples for a sophisticated look',
      palette: {
        primary: '#6B6BDD',
        secondary: '#8A8AEE',
        accent: '#AFAFFF',
        background: '#FFFFFF',
        text: '#6B6BDD',
        surface: '#F8F8FF',
        border: '#E5E5F5'
      },
      colors: ['#6B6BDD', '#8A8AEE', '#AFAFFF']
    },
    {
      id: 'nature-greens',
      name: 'Nature Greens',
      description: 'Fresh greens inspired by nature',
      palette: {
        primary: '#2D5016',
        secondary: '#4A6926',
        accent: '#7CB342',
        background: '#FFFFFF',
        text: '#2D5016',
        surface: '#F1F8E9',
        border: '#E8F5E8'
      },
      colors: ['#2D5016', '#4A6926', '#7CB342']
    },
    {
      id: 'ocean-blues',
      name: 'Ocean Blues',
      description: 'Calming blues reminiscent of the ocean',
      palette: {
        primary: '#2C5AA0',
        secondary: '#3D6BB3',
        accent: '#5B9BD5',
        background: '#FFFFFF',
        text: '#2C5AA0',
        surface: '#F0F4FF',
        border: '#D1E5F7'
      },
      colors: ['#2C5AA0', '#3D6BB3', '#5B9BD5']
    },
    {
      id: 'sunset-orange',
      name: 'Sunset Orange',
      description: 'Warm oranges reminiscent of sunset',
      palette: {
        primary: '#F57C00',
        secondary: '#FB8C00',
        accent: '#FFB74D',
        background: '#FFFFFF',
        text: '#F57C00',
        surface: '#FFF3E0',
        border: '#FFE0B2'
      },
      colors: ['#F57C00', '#FB8C00', '#FFB74D']
    },
    {
      id: 'black-white-minimal',
      name: 'Black & White Minimal ⚫⚪',
      description: 'Striking black and white minimalist aesthetic',
      palette: {
        primary: '#000000',
        secondary: '#333333',
        accent: '#666666',
        background: '#FFFFFF',
        text: '#000000',
        surface: '#F5F5F5',
        border: '#CCCCCC'
      },
      colors: ['#000000', '#333333', '#666666', '#999999', '#CCCCCC']
    },
    {
      id: 'greyscale-elegant',
      name: 'Greyscale Elegant 📸',
      description: 'Sophisticated greyscale palette for classic elegance',
      palette: {
        primary: '#424242',
        secondary: '#616161',
        accent: '#757575',
        background: '#FAFAFA',
        text: '#2C2C2C',
        surface: '#F5F5F5',
        border: '#BDBDBD'
      },
      colors: ['#424242', '#616161', '#757575', '#9E9E9E', '#BDBDBD']
    },
    {
      id: 'high-contrast-bw',
      name: 'High Contrast B&W ⚡',
      description: 'Maximum contrast black and white for accessibility and impact',
      palette: {
        primary: '#000000',
        secondary: '#000000',
        accent: '#FFFFFF',
        background: '#FFFFFF',
        text: '#000000',
        surface: '#FFFFFF',
        border: '#000000'
      },
      colors: ['#000000', '#FFFFFF']
    }
  ];

  const [selectedPreset, setSelectedPreset] = useState<string>('classic-warm');
  const [currentSeason, setCurrentSeason] = useState<string>('');

  // Check for seasonal themes and special effects
  useEffect(() => {
    const themedPresets = ['christmas-festive', 'spring-bloom', 'summer-sunshine', 'autumn-warmth', 'winter-magic'];
    const minimalPresets = ['black-white-minimal', 'greyscale-elegant', 'high-contrast-bw'];
    const themedActive = themedPresets.includes(selectedPreset);
    const minimalActive = minimalPresets.includes(selectedPreset);
    const body = document.body;
    
    if (themedActive) {
      body.classList.add('christmas-theme-active');
      if (selectedPreset.includes('spring')) {
        body.classList.add('spring-theme-active');
      } else if (selectedPreset.includes('summer')) {
        body.classList.add('summer-theme-active');
      } else if (selectedPreset.includes('autumn')) {
        body.classList.add('autumn-theme-active');
      } else if (selectedPreset.includes('winter')) {
        body.classList.add('winter-theme-active');
      }
    } else if (minimalActive) {
      if (selectedPreset === 'black-white-minimal') {
        body.classList.add('minimal-black-white-active');
      } else if (selectedPreset === 'greyscale-elegant') {
        body.classList.add('greyscale-elegant-active');
      } else if (selectedPreset === 'high-contrast-bw') {
        body.classList.add('high-contrast-bw-active');
      }
    } else {
      // Clean all seasonal classes first
      body.classList.remove('christmas-theme-active', 'spring-theme-active', 'summer-theme-active', 'autumn-theme-active', 'winter-theme-active', 'minimal-black-white-active', 'greyscale-elegant-active', 'high-contrast-bw-active');
    }
    
    // Cleanup on unmount
    return () => {
      body.classList.remove('christmas-theme-active', 'spring-theme-active', 'summer-theme-active', 'autumn-theme-active', 'winter-theme-active', 'minimal-black-white-active', 'greyscale-elegant-active', 'high-contrast-bw-active');
    };
  }, [selectedPreset]);

  // Auto-detect current season
  useEffect(() => {
    const getCurrentSeason = () => {
      const date = new Date();
      const month = date.getMonth() + 1; // 1-12
      
      if (month >= 3 && month <= 5) return 'spring';
      if (month >= 6 && month <= 8) return 'summer';
      if (month >= 9 && month <= 11) return 'autumn';
      return 'winter';
    };
    
    setCurrentSeason(getCurrentSeason());
  }, []);

  // Load current theme when component mounts
  useEffect(() => {
    loadCurrentTheme();
  }, []);

  const loadCurrentTheme = () => {
    const savedTheme = localStorage.getItem('admin-theme');
    if (savedTheme) {
      try {
        const theme = JSON.parse(savedTheme);
        setColorPalette(theme.palette);
        if (theme.presetId) {
          setSelectedPreset(theme.presetId);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        // If theme is corrupted, set high contrast as default
        setHighContrastDefault();
      }
    } else {
      // No theme saved, set high contrast as default
      setHighContrastDefault();
    }
  };

  const setHighContrastDefault = () => {
    const highContrastPreset = themePresets.find(p => p.id === 'high-contrast-bw');
    if (highContrastPreset) {
      setColorPalette(highContrastPreset.palette);
      setSelectedPreset('high-contrast-bw');
      applyTheme(highContrastPreset.palette);
    }
  };

  const updateColor = (property: keyof ColorPalette, value: string) => {
    const newPalette = { ...colorPalette, [property]: value };
    setColorPalette(newPalette);
    applyTheme({ [property]: value });
  };

  const applyTheme = (updates?: Partial<ColorPalette>) => {
    const finalPalette = updates ? { ...colorPalette, ...updates } : colorPalette;
    
    // Apply CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--color-primary', finalPalette.primary);
    root.style.setProperty('--color-secondary', finalPalette.secondary);
    root.style.setProperty('--color-accent', finalPalette.accent);
    root.style.setProperty('--color-background', finalPalette.background);
    root.style.setProperty('--color-text', finalPalette.text);
    root.style.setProperty('--color-surface', finalPalette.surface);
    root.style.setProperty('--color-border', finalPalette.border);
    
    // Save to localStorage and trigger theme update event
    const themeData = {
      palette: finalPalette,
      presetId: selectedPreset,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem('admin-theme', JSON.stringify(themeData));
    
    // Dispatch theme updated event for home page updates
    window.dispatchEvent(new CustomEvent('theme_updated', { detail: themeData }));
  };

  const applyPreset = (preset: ThemePreset) => {
    setColorPalette(preset.palette);
    setSelectedPreset(preset.id);
    setSelectedSwatch('primary');
    applyTheme(preset.palette);
  };

  const resetTheme = () => {
    const resetPalette = themePresets[0].palette;
    setColorPalette(resetPalette);
    setSelectedPreset('classic-warm');
    applyTheme(resetPalette);
    // Trigger theme update event for live changes on home page
    window.dispatchEvent(new CustomEvent('theme_updated', { detail: { palette: resetPalette, presetId: 'classic-warm' } }));
  };

  const saveTheme = () => {
    applyTheme();
    alert('Theme saved successfully! Your brand colors have been applied throughout the app.');
  };

  const previewTheme = () => {
    applyTheme();
    alert('Preview applied! Check the interface to see your theme changes.');
  };

  return (
    <section className="theme-management">
      <div className="style-gallery-header">
        <h2>Colours</h2>
        <p>Customize your app's color scheme to perfectly match your brand. Choose from preset themes or create your own unique palette.</p>
      </div>

      {/* Season Auto-Detection Notice */}
      {currentSeason && (
        <div className="season-notice">
          <div className="season-indicator">
            <i className="fas fa-calendar-alt"></i>
            <span>Detected season: <strong>{currentSeason.charAt(0).toUpperCase() + currentSeason.slice(1)}</strong></span>
          </div>
          <button 
            onClick={() => {
              const seasonalThemes: Record<string, string> = {
                spring: 'spring-bloom',
                summer: 'summer-sunshine', 
                autumn: 'autumn-warmth',
                winter: 'winter-magic'
              };
              const themeToApply = seasonalThemes[currentSeason];
              if (themeToApply) {
                const preset = themePresets.find(p => p.id === themeToApply);
                if (preset) applyPreset(preset);
              }
            }}
            className="season-auto-btn"
          >
            Apply {currentSeason.charAt(0).toUpperCase() + currentSeason.slice(1)} Theme
          </button>
        </div>
      )}

      {/* Theme Presets */}
      <div className="theme-presets">
        <h3 className="section-title">Theme Presets</h3>
        <div className="preset-grid">
          {themePresets.map((preset) => (
            <div
              key={preset.id}
              className={`preset-card ${selectedPreset === preset.id ? 'selected' : ''}`}
              data-theme={preset.id}
              onClick={() => applyPreset(preset)}
            >
              <h4 className="preset-title">{preset.name}</h4>
              <div className={`preset-colors ${
                preset.id === 'christmas-festive' ? 'christmas-color-palette' :
                preset.id.includes('spring') ? 'spring-color-palette' :
                preset.id.includes('summer') ? 'summer-color-palette' :
                preset.id.includes('autumn') ? 'autumn-color-palette' :
                preset.id.includes('winter') ? 'winter-color-palette' : ''
              }`}>
                {preset.colors.map((color, index) => (
                  <div
                    key={index}
                    className="preset-color"
                    style={{ backgroundColor: color }}
                  />
                ))}
                {preset.id === 'christmas-festive' && (
                  <>
                    <div className="preset-color">🎄</div>
                    <div className="preset-color">❄️</div>
                    <div className="preset-color">✨</div>
                  </>
                )}
                {preset.id === 'spring-bloom' && (
                  <>
                    <div className="preset-color">🌸</div>
                    <div className="preset-color">🌿</div>
                    <div className="preset-color">🦋</div>
                  </>
                )}
                {preset.id === 'summer-sunshine' && (
                  <>
                    <div className="preset-color">🌻</div>
                    <div className="preset-color">☀️</div>
                    <div className="preset-color">🌊</div>
                  </>
                )}
                {preset.id === 'autumn-warmth' && (
                  <>
                    <div className="preset-color">🍂</div>
                    <div className="preset-color">🌰</div>
                    <div className="preset-color">🎯</div>
                  </>
                )}
                {preset.id === 'winter-magic' && (
                  <>
                    <div className="preset-color">❄️</div>
                    <div className="preset-color">🌟</div>
                    <div className="preset-color">⛄</div>
                  </>
                )}
              </div>
              <p className="preset-description">{preset.description}</p>
              {preset.id === 'christmas-festive' && (
                <div className="christmas-sparkles">
                  ★ ✨ ⭐ 💫 🌟
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Color Palette Editor */}
      <div className="color-palette-section">
        <h3 className="section-title">Customize Colors</h3>
        
        <div className="color-categories">
          {Object.entries(colorPalette).map(([key, value]) => (
            <div key={key} className="category-row">
              <div className="category-label">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </div>
              <div className="color-input-group">
                <input
                  type="color"
                  value={value}
                  onChange={(e) => updateColor(key as keyof ColorPalette, e.target.value)}
                  className="color-input"
                />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    if (/^#[0-9A-F]{6}$/i.test(e.target.value) || e.target.value === '') {
                      updateColor(key as keyof ColorPalette, e.target.value);
                    }
                  }}
                  className="hex-input"
                  placeholder="#000000"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Live Color Swatches */}
        <div className="color-swatches">
          {Object.entries(colorPalette).map(([key, value]) => (
            <div
              key={key}
              className={`color-swatch ${selectedSwatch === key ? 'active' : ''}`}
              onClick={() => setSelectedSwatch(key)}
            >
              <div
                className="color-preview"
                style={{ backgroundColor: value }}
              />
              <div className="color-name">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </div>
              <div className="color-hex">{value}</div>
            </div>
          ))}
        </div>

        {/* Live Preview */}
        <div className="live-preview">
          <h4 style={{ color: colorPalette.text, marginBottom: '1rem' }}>
            Live Preview
          </h4>
          <div className="preview-tile">
            <button
              className="preview-button"
              style={{
                backgroundColor: colorPalette.primary,
                color: 'white',
                borderRadius: '12px'
              }}
            >
              Primary Button
            </button>
            <button
              className="preview-button"
              style={{
                backgroundColor: colorPalette.secondary,
                color: 'white',
                borderRadius: '12px'
              }}
            >
              Secondary Button
            </button>
            <button
              className="preview-button"
              style={{
                backgroundColor: colorPalette.accent,
                color: 'white',
                borderRadius: '12px'
              }}
            >
              Accent Button
            </button>
          </div>
          <div className="preview-card" style={{ backgroundColor: colorPalette.surface }}>
            <p className="preview-text" style={{ color: colorPalette.text }}>
              Sample text showing how your colors work together
            </p>
          </div>
        </div>
      </div>

      {/* Theme Actions */}
      <div className="theme-actions">
        <button
          className="action-btn btn-primary"
          onClick={previewTheme}
        >
          ✨ Preview Theme
        </button>
        <button
          className="action-btn btn-success"
          onClick={saveTheme}
        >
          💾 Save Theme
        </button>
        <button
          className="action-btn btn-warning"
          onClick={resetTheme}
        >
          🔄 Reset to Default
        </button>
      </div>
    </section>
  );
};

export default ThemeManagement;
